import { useRef, useState } from "react";
import { Download, ImageIcon, Paperclip, RotateCcw, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { CopyButton, EmptyState, ErrorBanner, PageHeader, Spinner, fmt, useLoad } from "../../components/kit";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { downloadFile, fmtSize, uploadFile } from "../../lib/files";
import { useT } from "../../lib/i18n";

const KINDS = [["attachment", "files.att", Paperclip, "files.atthelp"], ["image", "files.img", ImageIcon, "files.imghelp"]];

function uploadLabel(pct, compact, t) {
  if (pct !== null) return `${t("Uploading")} ${pct}%`;
  return compact ? t("Upload") : t("files.choose");
}

export function UploadBox({ kind, onDone, testId = "upload", compact }) {
  const { t } = useT();
  const ref = useRef(null);
  const [pct, setPct] = useState(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPct(0);
    try { const f = await uploadFile(file, kind, setPct); toast.success(`${f.filename} uploaded`); onDone?.(f); }
    catch (err) { toast.error(errMsg(err)); } finally { setPct(null); }
  };
  return (
    <div className={compact ? "" : "space-y-2"}>
      <input ref={ref} type="file" className="hidden" onChange={pick} accept={kind === "attachment" ? undefined : "image/png,image/jpeg,image/webp" + (kind === "image" ? ",image/gif" : "")} data-testid={`${testId}-input`} />
      <Button type="button" size="sm" variant={compact ? "outline" : "default"} disabled={pct !== null} onClick={() => ref.current?.click()}
        className={compact ? "border-white/10 bg-transparent hover:bg-white/5 hover:text-white" : ""} data-testid={`${testId}-button`}>
        <Upload className="mr-1.5 h-3.5 w-3.5" />{uploadLabel(pct, compact, t)}
      </Button>
      {pct !== null && !compact && <Progress value={pct} className="h-1 bg-white/5" data-testid={`${testId}-progress`} />}
    </div>
  );
}

function FileRow({ f, canEdit, reload }) {
  const { t } = useT();
  const del = async () => {
    if (!window.confirm(`Delete ${f.filename}? Emails that already went out are not affected.`)) return;
    try { await api.delete(`/app/files/${f.id}`); toast.success(t("File moved to the bin for 30 days")); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-col gap-2 border-t border-white/5 py-3 first:border-0 sm:flex-row sm:items-center sm:justify-between" data-testid={`file-row-${f.id}`}>
      <div className="flex min-w-0 items-center gap-3">
        {f.public_url ? <img src={f.public_url} alt="" className="h-9 w-9 rounded-md border border-white/10 object-cover" /> : <Paperclip className="h-9 w-9 rounded-md border border-white/10 p-2 text-zinc-500" />}
        <div className="min-w-0"><div className="truncate text-sm text-zinc-200">{f.filename}</div>
          <div className="text-xs text-zinc-500">{t(`kind.${f.kind}`)} · {fmtSize(f.size)} · {fmt(f.created_at)} · <span className="font-mono">{f.id}</span></div></div>
      </div>
      <div className="flex items-center gap-1">
        {f.public_url && <CopyButton value={f.public_url} label={t("Copy link")} testId={`file-copy-${f.id}`} />}
        <Button size="sm" variant="ghost" className="hover:bg-white/5 hover:text-white" onClick={() => downloadFile(f).catch((e) => toast.error(errMsg(e)))} data-testid={`file-download-${f.id}`}><Download className="h-4 w-4" /></Button>
        {canEdit && <Button size="sm" variant="ghost" className="text-red-300 hover:bg-red-500/10 hover:text-red-200" onClick={del} data-testid={`file-delete-${f.id}`}><Trash2 className="h-4 w-4" /></Button>}
      </div>
    </div>
  );
}

function daysLeft(iso) {
  return Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000));
}

function Bin() {
  const { t } = useT();
  const { data, error, loading, reload } = useLoad(() => api.get("/app/files/bin").then((r) => r.data));
  const restore = async (f) => {
    try { await api.post(`/app/files/${f.id}/restore`); toast.success(`${f.filename} restored`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (loading) return <Spinner />;
  return (
    <div data-testid="files-bin">
      <ErrorBanner error={error} />
      <p className="mb-4 text-xs text-zinc-500">{t("files.bininfo")}</p>
      {data?.length ? <div className="surface rounded-2xl px-5" data-testid="files-bin-list">{data.map((f) => (
        <div key={f.id} className="flex flex-col gap-3 border-t border-white/5 py-4 first:border-0 sm:flex-row sm:items-center sm:justify-between" data-testid={`bin-row-${f.id}`}>
          <div className="flex min-w-0 items-center gap-3"><Trash2 className="h-9 w-9 shrink-0 rounded-xl border border-white/10 p-2 text-zinc-500" />
            <div className="min-w-0"><div className="truncate text-sm text-zinc-200">{f.filename}</div>
              <div className="text-xs text-zinc-500">{t(`kind.${f.kind}`)} · {fmtSize(f.size)} · {t("deleted")} {fmt(f.deleted_at)}</div></div></div>
          <div className="flex items-center gap-3"><span className="whitespace-nowrap rounded-full border border-amber-500/25 px-2.5 py-0.5 text-[11px] text-amber-300" data-testid={`bin-days-${f.id}`}>{daysLeft(f.purge_at)} {t("files.daysleft")}</span>
            <Button size="sm" variant="outline" onClick={() => restore(f)} data-testid={`files-bin-restore-${f.id}`}><RotateCcw className="h-3.5 w-3.5" />{t("files.restore")}</Button></div>
        </div>))}</div>
        : <EmptyState icon={Trash2} title={t("files.binempty")} body={t("files.binemptybody")} testId="files-bin-empty" />}
    </div>
  );
}

function ActiveFiles() {
  const { t } = useT();
  const { can } = useAuth();
  const { data, error, loading, reload } = useLoad(() => api.get("/app/files").then((r) => r.data));
  return (
    <>
      {can("developer") && <div className="mb-6 grid gap-4 md:grid-cols-2">{KINDS.map(([k, name, Icon, help]) => (
        <div key={k} className="surface rounded-2xl p-6" data-testid={`files-upload-${k}`}>
          <div className="mb-1 flex items-center gap-2 text-sm font-medium text-white"><Icon className="h-4 w-4 text-purple-300" />{t(name)}</div>
          <p className="mb-5 text-xs text-zinc-500">{t(help)}</p>
          <UploadBox kind={k} onDone={reload} testId={`files-upload-${k}`} />
        </div>))}</div>}
      <ErrorBanner error={error} />
      {loading && <Spinner />}
      {!loading && data?.length > 0 && <div className="surface rounded-2xl px-5" data-testid="files-list">{data.map((f) => <FileRow key={f.id} f={f} canEdit={can("developer")} reload={reload} />)}</div>}
      {!loading && !data?.length && <EmptyState icon={Paperclip} title={t("files.none")} body={t("files.nonebody")} testId="files-empty" />}
      <p className="mt-4 text-xs text-zinc-500">{t("API: attach a file with")} <code className="text-purple-300">{'"attachments": [{"file_id": "file_…"}]'}</code>, {t("or send it inline as base64. See Docs.")}</p>
    </>
  );
}

export default function Files() {
  const { t } = useT();
  const { can } = useAuth();
  const [tab, setTab] = useState("files");
  return (
    <div>
      <PageHeader title={t("nav.files")} description={t("desc.files")} />
      {can("admin") && <div className="mb-6 inline-flex rounded-full border border-white/10 bg-white/[0.02] p-1" role="tablist">
        {[["files", t("files.tab"), Paperclip], ["bin", t("files.bin"), Trash2]].map(([k, l, Icon]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`files-tab-${k}`}
            className={`inline-flex min-h-[36px] items-center gap-1.5 rounded-full px-4 text-xs font-medium transition-colors ${tab === k ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}><Icon className="h-3.5 w-3.5" />{l}</button>))}
      </div>}
      {tab === "bin" ? <Bin /> : <ActiveFiles />}
    </div>
  );
}
